
"use client";

import type { PropsWithChildren } from 'react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { ShareSheetHost } from '@/components/share/share-sheet';
import {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  useSidebar,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { MessageSquare, Search as SearchIcon, Menu, HelpCircle, Heart, X } from "lucide-react";
import { MediaPlayer } from './media-player';
import { GlobalVuStrip } from '@/components/media/global-vu-strip';
import { GlobalCallOverlay } from '@/components/call/global-call-overlay';
import { ActiveCallUI } from '@/components/call/active-call-ui';
import { cn } from "@/lib/utils";
import { ChatWindow } from './chat-window';
import { SidebarNav } from './sidebar-nav';
import { Button } from '../ui/button';
import { FavoritesBar } from './favorites-bar';
import { LoggedOutHeaderContent } from './logged-out-header-content';
import { useHeaderBoardPrefs, useMenuSide } from '@/contexts/user-layout-prefs-context';
import { HeaderBoard } from './header-board';
import type { BoardItem } from '@/services/header-board';
import { PWADesktopReminder } from '@/components/pwa/intro-popup';
import { OutdatedBrowserBanner } from './outdated-browser-banner';
import Link from 'next/link';
import { useIsMobile } from '@/hooks/use-mobile';
import { useGuestThemeRotation } from '@/hooks/use-guest-theme-rotation';
import { useAuth } from '@/contexts/auth-context';
import { useMediaPlayer } from '@/contexts/media-player-context';
import { usePathname, useRouter } from 'next/navigation';
import { subscribeUnreadCount, subscribeChatRooms, addMembersToGroup } from '@/services/chat';
import { subscribeActiveCallForUser, endCall } from '@/services/calls';
import { useToast } from '@/hooks/use-toast';
import {
  playNotificationSound,
  getNotificationSoundPrefs,
  announceWays,
  type NotificationSoundPrefs,
  DEFAULT_PREFS as DEFAULT_SOUND_PREFS,
} from '@/services/notification-sound';
import { nativeBadge, nativeNotify } from '@/lib/native-bridge';
import { GuidedTour } from '@/components/tour/guided-tour';
import { ThemeIntroSlideshow } from '@/components/tour/theme-intro-slideshow';
import { GuestTracker } from '@/components/analytics/guest-tracker';
import { ThemePicker } from '@/components/themes/theme-picker';
import { HeaderAdBanner } from './header-ad-banner';
import { useBranding } from '@/contexts/branding-context';
import { useHelp } from '@/contexts/help-context';
import { HelpBubble } from '@/components/help/help-bubble';
import { AutoHelpLayer } from '@/components/help/auto-help-layer';
import { useSafeMode } from '@/lib/safe-mode';
import { installFullscreenRotation } from '@/lib/video-fullscreen';
import { usePerfExp } from '@/lib/perf-experiments';
import dynamic from 'next/dynamic';
import { restoreScrollTo, takeResumePoint } from '@/lib/scroll-resume';
import { useHoldDragSwap } from '@/hooks/use-hold-drag-swap';
import { CopiMic } from '@/components/copi/copi-mic';
import { ReceiptSnapHost } from '@/components/bookkeeping/receipt-snap-host';

const MobileFooterNav = dynamic(
  () => import('./mobile-footer-nav').then((m) => m.MobileFooterNav),
  { ssr: false }
);

interface FineTuneSettings {
  headerBgColor?: string;
  headerBgImage?: string;
  headerBgType?: string;
  menuBgColor?: string;
  menuBgImage?: string;
  menuBgType?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  bodyBgColor?: string;
  bodyBgImage?: string;
  bodyBgType?: string;
  borderColor?: string;
  borderColorFocus?: string;
  borderWidth?: string;
}


function AppShellLayout({ children }: PropsWithChildren) {
  const tChat = useTranslations('chat');
  const tNav = useTranslations('nav');
  const tCommon = useTranslations('common');
  // The VU column also shows while coπ's mic listens (global-vu-strip) — keep
  // the content clear of it then too.
  const [micLive, setMicLive] = useState(false);
  useEffect(() => {
    const sync = () => setMicLive((window as any).__bbMicOn === true);
    window.addEventListener('bb-mic', sync);
    return () => window.removeEventListener('bb-mic', sync);
  }, []);
  // A full-screen video turns on its side (lib/video-fullscreen).
  useEffect(() => installFullscreenRotation(), []);
  const { displayMode, openMobile, setOpenMobile } = useSidebar();
  const { user, userProfile, loading: authLoading } = useAuth();
  const menuSide = useMenuSide();
  const boardPrefs = useHeaderBoardPrefs();
  // Hold and drag menu / search / chat across the screen to swap sides.
  const swap = useHoldDragSwap();
  // The user's unread chats, for the header message board.
  const [boardChats, setBoardChats] = useState<BoardItem[]>([]);
  // A passing board message pushes an open favorites strip out ahead of it —
  // same direction, same speed — and the strip comes back right behind it
  // (Gordon 2026-10-30). Keyframes in page coordinates from the message's
  // start position (L0), width (w) and speed (v); F = the strip's width:
  //   tA  message reaches the strip's right edge → push starts
  //   tB  strip fully pushed out (moved −F)     → re-enter just behind the message (+w)
  //   tC  message's tail reaches the strip       → back at rest
  const favStripRef = useRef<HTMLDivElement>(null);
  const favAnim = useRef<Animation | null>(null);
  useEffect(() => {
    const onGlide = (e: Event) => {
      const strip = favStripRef.current, region = strip?.parentElement;
      if (!strip || !region || typeof strip.animate !== 'function') return;
      const { boardLeft, from, width, speed } = (e as CustomEvent<{ boardLeft: number; from: number; width: number; speed: number }>).detail;
      const fx = region.getBoundingClientRect().left, F = region.clientWidth;
      const L0 = boardLeft + from;
      const tA = Math.max(0, (L0 - (fx + F)) / speed);
      const tB = Math.max(tA, (L0 - fx) / speed);
      const tC = Math.max(tB + 0.01, (L0 + width - fx) / speed);
      favAnim.current?.cancel();
      favAnim.current = strip.animate([
        { transform: 'translateX(0px)', offset: 0 },
        { transform: 'translateX(0px)', offset: tA / tC },
        { transform: `translateX(${-F}px)`, offset: tB / tC },
        { transform: `translateX(${width}px)`, offset: Math.min(1, tB / tC + 0.0001) },
        { transform: 'translateX(0px)', offset: 1 },
      ], { duration: tC * 1000, easing: 'linear' });
    };
    const onPause = (e: Event) => {
      if ((e as CustomEvent<{ paused: boolean }>).detail?.paused) favAnim.current?.pause(); else favAnim.current?.play();
    };
    window.addEventListener('bb-board-glide', onGlide);
    window.addEventListener('bb-board-glide-pause', onPause);
    return () => {
      window.removeEventListener('bb-board-glide', onGlide);
      window.removeEventListener('bb-board-glide-pause', onPause);
      favAnim.current?.cancel();
    };
  }, []);
  const { isActive: isMediaPlayerActive, isDJMode, djTrackInfo, isPlaying } = useMediaPlayer();
  // Crash-loop recovery flag (set pre-boot by the inline head script after
  // two consecutive hard renderer deaths) — skips the heaviest chrome.
  const safeMode = useSafeMode();
  const perfExp = usePerfExp();
  const { settings: brandingSettings } = useBranding();
  const { helpEnabled, toggleHelp } = useHelp();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [initialChatRoomId, setInitialChatRoomId] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  const soundPrefsRef = useRef<NotificationSoundPrefs>(DEFAULT_SOUND_PREFS);
  // Tracks whether we've already restored chat state from localStorage for this session
  const chatRestoredRef = useRef(false);
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const router = useRouter();
  const { toast } = useToast();
  const [profileThemeClass, setProfileThemeClass] = useState('');
  const [fineTune, setFineTune] = useState<FineTuneSettings>({});
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStartStep, setTourStartStep] = useState(0);
  const [showSlideshow, setShowSlideshow] = useState(false);
  // Theme picker — rendered at root level so it's never clipped by the mobile sidebar Sheet
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  // Opening or closing the menu closes the theme panel (Gordon 2026-11-01).
  const sidebarSeen = useRef(false);
  useEffect(() => {
    if (!sidebarSeen.current) { sidebarSeen.current = true; return; }
    setThemePickerOpen(false);
  }, [displayMode, openMobile]);

  // Visitor chat info popup — shown when a logged-out user clicks the FAB
  const [guestChatPopup, setGuestChatPopup] = useState(false);

  // Mobile footer nav tray — slide-up icon menu above the player bar.
  // Swipe/drag gesture handling lives entirely inside MobileFooterNav now.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Chat FAB attention animation — fires every ~9 seconds for logged-out visitors only
  const [fabAnim, setFabAnim] = useState('');
  const fabAnimTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const FAB_ANIMS = useMemo(() => [
    '[animation:fab-pulse_0.5s_ease-in-out]',
    '[animation:fab-wiggle_0.5s_ease-in-out]',
    '[animation:fab-bounce_0.5s_ease-in-out]',
  ], []);

  useEffect(() => {
    if (user) { setFabAnim(''); return; } // No animation when logged in
    const trigger = () => {
      const pick = FAB_ANIMS[Math.floor(Math.random() * FAB_ANIMS.length)];
      setFabAnim(pick);
      setTimeout(() => setFabAnim(''), 600);
    };
    fabAnimTimerRef.current = setTimeout(function repeat() {
      trigger();
      fabAnimTimerRef.current = setTimeout(repeat, 9000);
    }, 9000);
    return () => { if (fabAnimTimerRef.current) clearTimeout(fabAnimTimerRef.current); };
  }, [user, FAB_ANIMS]);

  // Auto-rotate themes every N seconds for logged-out visitors (Super Admin configurable)
  useGuestThemeRotation();

  // ── Scroll position management ───────────────────────────────────────────
  // Persists to localStorage so scroll position survives a full page refresh.
  // Back/forward within 7 days → restore; new navigation → jump to top.
  const SCROLL_KEY  = 'bb_scroll_pos';
  const RESTORE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

  const isPopStateRef   = useRef(false);
  const isFirstRender   = useRef(true);

  // Page-memory policy — only paths matching a `rememberedPages` prefix
  // configured in Super Admin → Appearance keep their scroll position.
  // Everything else (e.g. /dashboard) starts at the top on every visit so
  // stale numbers don't mislead.
  const rememberedPages: string[] = Array.isArray((brandingSettings as any)?.rememberedPages)
    ? (brandingSettings as any).rememberedPages
    : [];
  const pageIsRemembered = (p: string | null): boolean => {
    if (!p || rememberedPages.length === 0) return false;
    return rememberedPages.some(prefix => prefix && (p === prefix || p.startsWith(prefix + '/')));
  };

  function savePos(path: string, y: number) {
    if (!pageIsRemembered(path)) return;
    try {
      const raw  = localStorage.getItem(SCROLL_KEY);
      const map: Record<string, { y: number; t: number }> = raw ? JSON.parse(raw) : {};
      map[path] = { y, t: Date.now() };
      // Trim to 50 most recent paths to avoid bloat
      const keys = Object.keys(map);
      if (keys.length > 50) {
        const sorted = keys.sort((a, b) => map[a].t - map[b].t);
        sorted.slice(0, keys.length - 50).forEach(k => delete map[k]);
      }
      localStorage.setItem(SCROLL_KEY, JSON.stringify(map));
    } catch { /* ignore */ }
  }

  function loadPos(path: string): number | null {
    if (!pageIsRemembered(path)) return null;
    try {
      const raw = localStorage.getItem(SCROLL_KEY);
      if (!raw) return null;
      const entry = (JSON.parse(raw) as Record<string, { y: number; t: number }>)[path];
      if (!entry || Date.now() - entry.t > RESTORE_TTL) return null;
      return entry.y;
    } catch { return null; }
  }

  // Mark back/forward navigations (popstate only fires for browser history, not Link clicks)
  useEffect(() => {
    const onPop = () => { isPopStateRef.current = true; };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // On first mount: back to exactly where you were after an app update (any
  // page), else the remembered position for this path. Keeps re-applying it
  // while the page's content loads in — a single early jump got clamped and
  // left people at the bottom (Gordon 2026-10-31).
  useEffect(() => {
    const saved = takeResumePoint() ?? loadPos(pathname);
    if (saved !== null && saved > 0) return restoreScrollTo(saved);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore chat-open state from localStorage once a logged-in user is confirmed.
  // Only runs once per mount (chatRestoredRef prevents re-triggering on uid changes).
  useEffect(() => {
    if (!user?.uid || chatRestoredRef.current) return;
    chatRestoredRef.current = true;
    try {
      if (localStorage.getItem('bb-chat-open') === 'true') setIsChatOpen(true);
    } catch { /* ignore storage errors */ }
  }, [user?.uid]);

  // Persist chat open/closed state so it survives a refresh.
  useEffect(() => {
    if (!user?.uid) return;
    try { localStorage.setItem('bb-chat-open', String(isChatOpen)); } catch { /* ignore */ }
  }, [isChatOpen, user?.uid]);

  // Broadcast chat open/closed state so out-of-tree floating controls (e.g. the
  // super-admin "View As" eye button in ImpersonationBar, mounted in the root
  // layout) can get out of the way of the chat window's Send button.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.dataset.chatOpen = String(isChatOpen);
    window.dispatchEvent(new CustomEvent('bb-chat-open-change', { detail: { open: isChatOpen } }));
  }, [isChatOpen]);

  // Open chat to a specific room from anywhere via custom event
  useEffect(() => {
    const onOpenChat = (e: Event) => {
      const roomId = (e as CustomEvent<{ roomId: string }>).detail?.roomId;
      if (roomId) setInitialChatRoomId(roomId);
      setIsChatOpen(true);
    };
    window.addEventListener('openChatRoom', onOpenChat);
    return () => window.removeEventListener('openChatRoom', onOpenChat);
  }, []);

  // BB Copilot from anywhere (coπ voice mic, Gordon 2026-11-01: "call up all
  // the more admin stuff … add a contributor on a track and give the person a
  // %"): `window.dispatchEvent(new CustomEvent('bb-copilot', { detail: { prompt } }))`
  // opens the chat on its Copilot tab, Copilot domain, and sends `prompt` as
  // the user's message — the plan card then needs the usual Apply tap.
  // Signed out → sign-in, back to this page after.
  const [copilotRequest, setCopilotRequest] = useState<{ prompt: string; id: number } | null>(null);
  const pendingCopilotRef = useRef<string | null>(null);
  const startCopilot = useCallback((prompt: string) => {
    if (!user) {
      const here = window.location.pathname + window.location.search;
      router.push(`/signin?redirect=${encodeURIComponent(here)}`);
      return;
    }
    setOpenMobile(false);
    setThemePickerOpen(false);
    if (isMobile) window.dispatchEvent(new Event('close-player-sheet'));
    setCopilotRequest({ prompt, id: Date.now() });
    setIsChatOpen(true);
  }, [user, router, isMobile, setOpenMobile]);
  useEffect(() => {
    const onCopilot = (e: Event) => {
      const prompt = String((e as CustomEvent<{ prompt?: string }>).detail?.prompt ?? '').trim().slice(0, 1000);
      if (!prompt) return;
      e.preventDefault(); // tells coπ Copilot answered (else it opens /creator-ai)
      // Auth still resolving on a fresh load — hold the request until it has.
      if (authLoading) { pendingCopilotRef.current = prompt; return; }
      startCopilot(prompt);
    };
    window.addEventListener('bb-copilot', onCopilot);
    return () => window.removeEventListener('bb-copilot', onCopilot);
  }, [authLoading, startCopilot]);
  useEffect(() => {
    if (authLoading || !pendingCopilotRef.current) return;
    const prompt = pendingCopilotRef.current;
    pendingCopilotRef.current = null;
    startCopilot(prompt);
  }, [authLoading, startCopilot]);

  // Deep-link: anyone visiting any BB URL with `?room=<id>` lands straight
  // inside the matching chat room. Used by the Share Group Link button + by any
  // future invite flow. Removes the param once consumed so refreshes don't
  // re-trigger the room open.
  //
  // A logged-out visitor previously hit a dead end here — this effect set
  // isChatOpen/initialChatRoomId, but ChatWindow only renders `{user && ...}`,
  // so nothing happened and the link looked broken. And even a logged-in
  // non-member just got dropped into a room's messages without actually
  // being a participant. Gordon 2026-09-30: "share an invite to a BB chat to
  // a social media messenger, so they can just sign up or in quick and see
  // the chat directly" — bounce signed-out visitors to sign-in/up first
  // (same return-path sessionStorage key useRequireAuth already uses, so
  // /signin and the onboarding "done" step both bring them straight back
  // here), then auto-join them as a real participant once authenticated.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const roomId = params.get('room');
    if (!roomId) return;

    if (!user) {
      sessionStorage.setItem('auth_return_to', window.location.pathname + window.location.search);
      router.push('/signin');
      return;
    }

    addMembersToGroup(roomId, [{
      id: user.uid,
      name: userProfile?.displayName || user.displayName || 'New member',
      avatar: userProfile?.profileImageUrl || '',
    }]).catch(() => { /* already a participant, or the room's gone — still try to open it */ })
      .finally(() => {
        setInitialChatRoomId(roomId);
        setIsChatOpen(true);
      });

    params.delete('room');
    const q = params.toString();
    const cleanUrl = window.location.pathname + (q ? `?${q}` : '') + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
  }, [pathname, user, userProfile, router]);

  // Persist current page's scroll position to localStorage — DEBOUNCED.
  // savePos does a synchronous read+JSON.parse+mutate+JSON.stringify+write on
  // every call; calling it on every raw `scroll` event (which can fire at
  // 60-120Hz during a fling on iOS) blocked the main thread repeatedly enough
  // that iOS Safari's watchdog was killing the page — "A problem repeatedly
  // occurred" — specifically while scrolling a remembered page. We only need
  // an eventually-consistent last-known position, so write ~250ms after
  // scrolling settles instead of on every tick; a final flush on cleanup
  // (pathname change / unmount) still captures the last position reliably.
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    let lastY = window.scrollY;
    const onScroll = () => {
      lastY = window.scrollY;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => savePos(pathname, lastY), 250);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        savePos(pathname, lastY); // flush the last position so it isn't lost
      }
    };
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // On pathname change: restore saved pos (back/forward) or jump to top (new nav)
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (isMobile) setIsChatOpen(false);

    if (isPopStateRef.current) {
      isPopStateRef.current = false;
      const saved = loadPos(pathname);
      if (saved !== null) {
        requestAnimationFrame(() => window.scrollTo({ top: saved, left: 0, behavior: 'instant' }));
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for the sidebar "Themes" button — rendered at root level so it works on mobile
  useEffect(() => {
    // The Themes menu item toggles the panel (Gordon 2026-11-01).
    const handler = () => setThemePickerOpen(o => !o);
    window.addEventListener('bb-open-theme-picker', handler);
    return () => window.removeEventListener('bb-open-theme-picker', handler);
  }, []);

  // ── Firestore assertion crash guard ──────────────────────────────────────
  // The Firestore SDK v10 can emit an INTERNAL ASSERTION FAILED error during
  // live-sync when a document lands with version -1 (uninitialized sentinel).
  // This corrupts internal SDK state and cannot be recovered with React reset()
  // — only a full page reload reinitialises the SDK.  We install both an
  // unhandledrejection and a global error listener so we catch it whether it
  // surfaces as a rejected promise or a thrown error.
  useEffect(() => {
    const isFirestoreAssert = (msg: string) =>
      typeof msg === 'string' &&
      msg.includes('FIRESTORE') &&
      msg.includes('INTERNAL ASSERTION FAILED');

    let reloading = false;
    const reload = () => {
      if (reloading) return;
      reloading = true;
      // ── Loop breaker ──────────────────────────────────────────────────────
      // The reload only helps if the assertion was a one-off. If it recurs on
      // the fresh load (common on iOS Safari/PWA), reloading every time creates
      // an infinite crash loop → iOS "A problem repeatedly occurred" / "Can't
      // open this page" at startup. Cap reloads to 2 within a 60s window, then
      // give up and let the app render (degraded) instead of looping forever.
      try {
        const KEY = 'bb-fs-reload';
        const now = Date.now();
        const prev = JSON.parse(sessionStorage.getItem(KEY) || '{"n":0,"t":0}');
        const within = now - (prev.t || 0) < 60000;
        const n = within ? (prev.n || 0) + 1 : 1;
        sessionStorage.setItem(KEY, JSON.stringify({ n, t: now }));
        if (n > 2) {
          console.error('[AppShell] Firestore assertion recurring — loop breaker engaged, NOT reloading.');
          return;
        }
      } catch {
        // sessionStorage unavailable → we cannot count attempts, so an
        // unconditional reload here WOULD loop forever (each fresh boot
        // hits the same assertion, reloads again, repeat → iOS "A problem
        // repeatedly occurred"). A degraded-but-visible page beats a loop.
        console.error('[AppShell] Firestore assertion detected but sessionStorage unavailable — NOT reloading.');
        return;
      }
      console.warn('[AppShell] Firestore assertion detected — reloading to recover.');
      try { (window as any).__bbSend?.('reload-fsassert', {}); } catch { /* ignore */ }
      window.location.reload();
    };

    const onUnhandled = (e: PromiseRejectionEvent) => {
      if (isFirestoreAssert(e.reason?.message || String(e.reason || ''))) {
        e.preventDefault();
        reload();
      }
    };
    const onError = (e: ErrorEvent) => {
      if (isFirestoreAssert(e.message || '')) {
        e.preventDefault();
        reload();
      }
    };

    window.addEventListener('unhandledrejection', onUnhandled);
    window.addEventListener('error', onError);
    return () => {
      window.removeEventListener('unhandledrejection', onUnhandled);
      window.removeEventListener('error', onError);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Anti-FOUC: apply dark class ONCE on mount before theme context loads.
  // The theme context (applyThemeTokensToCss) takes over from here and is the
  // SOLE owner of the dark class at runtime. No ongoing listener here —
  // having two systems fight over the dark class causes the button-flicker bug.
  useEffect(() => {
    // Only act if theme context hasn't already applied inline styles
    const root = document.documentElement;
    if (root.style.getPropertyValue('--background')) return;
    // Visitors follow the system setting (Gordon 2026-11-02).
    const savedMode = localStorage.getItem('bb-signed-in') !== '1' ? 'system' : (localStorage.getItem('bb-theme-mode') || 'system');
    if (savedMode === 'system') {
      root.classList.toggle('dark', window.matchMedia('(prefers-color-scheme: dark)').matches);
    } else {
      root.classList.toggle('dark', savedMode === 'dark');
    }
    // ↑ No change-listener here. Theme context owns the dark class going forward.
  }, []);

  // Listen for profile theme changes via custom event or localStorage
  const isProfilePage = pathname === '/profile' || pathname?.startsWith('/profile/');

  useEffect(() => {
    if (!isProfilePage) {
      setProfileThemeClass('');
      setFineTune({});
      return;
    }
    // Read theme from localStorage (set by profile page)
    const stored = localStorage.getItem('bb-profile-theme-class');
    if (stored) setProfileThemeClass(stored);
    try {
      const ftStored = localStorage.getItem('bb-fine-tune');
      if (ftStored) setFineTune(JSON.parse(ftStored));
    } catch { /* ignore */ }

    // Listen for custom event from profile page theme changes
    const handleThemeChange = (e: CustomEvent) => {
      setProfileThemeClass(e.detail?.themeClass || '');
      if (e.detail?.fineTune) setFineTune(e.detail.fineTune);
    };
    window.addEventListener('profile-theme-change', handleThemeChange as EventListener);
    return () => window.removeEventListener('profile-theme-change', handleThemeChange as EventListener);
  }, [isProfilePage]);

  // Load notification sound prefs
  useEffect(() => {
    if (!user?.uid) return;
    getNotificationSoundPrefs(user.uid).then((prefs) => {
      soundPrefsRef.current = prefs;
    }).catch(() => {}); // fallback to defaults
    // Changed in chat settings → applies to the next message, no reload.
    const onPrefs = (e: Event) => { const p = (e as CustomEvent).detail; if (p) soundPrefsRef.current = p; };
    window.addEventListener('bb-notif-prefs', onPrefs);
    return () => window.removeEventListener('bb-notif-prefs', onPrefs);
  }, [user?.uid]);

  // Subscribe to unread message count + fire toast on new message
  useEffect(() => {
    if (!user?.uid) return;

    // Track previous room state so we can detect genuinely new messages
    let prevRoomSnapshots: Map<string, { lastMessageAt: number; lastSenderId: string }> = new Map();
    let initialized = false; // skip toast on first snapshot (catch-up on load)

    const unsub = subscribeChatRooms(user.uid, (rooms) => {
      // Recompute unread count
      let total = 0;
      const newSnapshots = new Map<string, { lastMessageAt: number; lastSenderId: string }>();

      for (const room of rooms) {
        const lastMsgMs: number = (room.lastMessageAt as any)?.toMillis?.() || 0;
        const lastSenderId: string = (room as any).lastSenderId || '';
        newSnapshots.set(room.id, { lastMessageAt: lastMsgMs, lastSenderId });

        if (lastSenderId && lastSenderId !== user.uid && lastMsgMs > 0) {
          const lastRead = (room.lastReadAt as any)?.[user.uid];
          const lastReadMs: number = lastRead?.toMillis?.() || 0;
          if (lastMsgMs > lastReadMs) {
            total++;

            // Toast only when: initialized + new message arrived + chat is not open to this room
            if (initialized) {
              const prev = prevRoomSnapshots.get(room.id);
              const isNewMsg = !prev || lastMsgMs > prev.lastMessageAt;
              if (isNewMsg) {
                const senderName = (room as any).lastSenderName || 'Someone';
                const preview = (room as any).lastMessageText
                  ? ((room as any).lastMessageText as string).slice(0, 60)
                  : 'sent you a message';
                toast({
                  title: `💬 ${senderName}`,
                  description: preview,
                  duration: 5000,
                });
                // The sound, or coπ saying it (Gordon 2026-11-01: "copi makes a chat
                // notification … a voice phrase or sound, then read the message").
                const ways = announceWays(soundPrefsRef.current);
                if (ways.sound) playNotificationSound(soundPrefsRef.current);
                if (ways.vibrate && soundPrefsRef.current.enabled) { try { navigator.vibrate?.([90, 60, 90]); } catch { /* not supported (iPhone) */ } }
                window.dispatchEvent(new CustomEvent('bb-chat-new', {
                  detail: {
                    roomId: room.id, senderName, preview, voice: ways.voice && soundPrefsRef.current.enabled,
                    // A booking request: coπ always says it and offers to open it.
                    ...((room as any).lastKind === 'booking' ? { kind: 'booking', link: (room as any).lastLink } : {}),
                  },
                }));
                // In the Mac app: a macOS notification that opens this chat.
                nativeNotify({
                  title: senderName,
                  body: preview,
                  tag: `chat-${room.id}`,
                  event: 'openChatRoom',
                  detail: { roomId: room.id },
                });
              }
            }
          }
        }
      }

      setUnreadCount(total);
      nativeBadge(total);
      // coπ "read the message" reads the newest unread chat.
      const newest = rooms
        .filter(room => { const sender = (room as any).lastSenderId || ''; return sender && sender !== user.uid; })
        .sort((a, b) => (((b.lastMessageAt as any)?.toMillis?.() || 0) - ((a.lastMessageAt as any)?.toMillis?.() || 0)))[0];
      (window as any).__bbLastChatRoom = newest?.id || null;
      // coπ "what are my last messages" / "write to Ski …": the person's rooms, newest first.
      (window as any).__bbChatRooms = [...rooms]
        .sort((a, b) => (((b.lastMessageAt as any)?.toMillis?.() || 0) - ((a.lastMessageAt as any)?.toMillis?.() || 0)))
        .slice(0, 60)
        .map(room => ({
          id: room.id, type: room.type, groupName: (room as any).groupName || '',
          names: (room as any).participantNames || {},
          lastSenderId: (room as any).lastSenderId || '', lastSenderName: (room as any).lastSenderName || '',
          lastText: String((room as any).lastMessageText || room.lastMessage || '').slice(0, 200),
        }));
      setBoardChats(rooms
        .filter(room => {
          const last: number = (room.lastMessageAt as any)?.toMillis?.() || 0;
          const read: number = (room.lastReadAt as any)?.[user.uid]?.toMillis?.() || 0;
          const sender = (room as any).lastSenderId || '';
          return sender && sender !== user.uid && last > read;
        })
        .sort((a, b) => (((b.lastMessageAt as any)?.toMillis?.() || 0) - ((a.lastMessageAt as any)?.toMillis?.() || 0)))
        .slice(0, 8)
        .map(room => ({
          id: `chat-${room.id}-${(room.lastMessageAt as any)?.toMillis?.() || 0}`,
          kind: 'chat' as const,
          sub: (room as any).lastSenderName || (room.type === 'group' ? room.groupName : '') || '',
          text: String((room as any).lastMessageText || room.lastMessage || '').slice(0, 120),
          personId: (room as any).lastSenderId || undefined,
          imageUrl: room.participantAvatars?.[(room as any).lastSenderId] || undefined,
          event: { name: 'openChatRoom', detail: { roomId: room.id } },
        })));
      prevRoomSnapshots = newSnapshots;
      initialized = true;
    });

    // Signing out (or switching user) clears the Dock badge too.
    return () => { unsub(); nativeBadge(0); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // Subscribe to active WebRTC calls — mounts ActiveCallUI globally on any page.
  // sessionStorage is cleared on every page refresh/new tab, so any "active" call
  // found in Firestore that was NOT started in this session is stale → auto-end it.
  useEffect(() => {
    if (!user?.uid) return;
    const SESSION_KEY = 'bb-active-call-id';
    const unsub = subscribeActiveCallForUser(user.uid, (call) => {
      if (!call) {
        sessionStorage.removeItem(SESSION_KEY);
        setActiveCallId(null);
        return;
      }
      const sessionCallId = sessionStorage.getItem(SESSION_KEY);
      if (sessionCallId === call.id) {
        // This call was started in the current session — show it
        setActiveCallId(call.id);
      } else {
        // Stale call from a previous session — silently end it in Firestore
        endCall(call.id, user.uid).catch(() => {});
        setActiveCallId(null);
      }
    });
    return unsub;
  }, [user?.uid]);

  // Listen for open-chat-window event from profile/artist pages
  const [initialChatTargetUserId, setInitialChatTargetUserId] = useState<string | null>(null);
  useEffect(() => {
    const handleOpenChat = (e: Event) => {
      const roomId = (e as CustomEvent).detail?.roomId ?? null;
      const targetUserId = (e as CustomEvent).detail?.targetUserId ?? null;
      setInitialChatRoomId(roomId);
      if (targetUserId) setInitialChatTargetUserId(targetUserId);
      setIsChatOpen(true);
    };
    window.addEventListener('open-chat-window', handleOpenChat);
    return () => window.removeEventListener('open-chat-window', handleOpenChat);
  }, []);

  // Listen for bb:launch-tour event dispatched from Help section or anywhere
  useEffect(() => {
    const handleLaunchTour = (e: Event) => {
      const stepIndex = (e as CustomEvent).detail?.stepIndex ?? 0;
      setTourStartStep(stepIndex);
      setTourOpen(true);
    };
    window.addEventListener('bb:launch-tour', handleLaunchTour);
    return () => window.removeEventListener('bb:launch-tour', handleLaunchTour);
  }, []);

  // Slideshow disabled (the WelcomeModal that replaced it is gone too — see the hero intro)
  // (showSlideshow stays false permanently; the state and ThemeIntroSlideshow are kept
  //  but never activated so the import can be removed gradually)
  useEffect(() => {
    setShowSlideshow(false);
  }, [user]);

  // ── Header favorites bar auto-scroll hint ────────────────────────────────
  // On first load: wait 1.5 s, scroll right ~160 px over 3 s, then scroll back.
  // Teaches visitors the bar is horizontally scrollable.
  const headerBarRef = useRef<HTMLDivElement>(null);

  // Show / hide the favorites strip in the header (Gordon 2026-10-30: "a show
  // favorites header button next to app menu button that opens/closes
  // favorites"). Per-device convenience → localStorage; default shown.
  // Visitors start with it closed (Gordon 2026-10-30) so the message board
  // has the header; signed-in users start with it open.
  const [favoritesChoice, setFavoritesChoice] = useState<'shown' | 'hidden' | null>(null);
  useEffect(() => {
    try {
      const v = localStorage.getItem('bb-header-favorites');
      if (v === 'shown' || v === 'hidden') setFavoritesChoice(v);
    } catch { /* storage blocked */ }
  }, []);
  const favoritesShown = favoritesChoice ? favoritesChoice === 'shown' : !!user;
  const toggleFavorites = useCallback(() => {
    const next = favoritesShown ? 'hidden' : 'shown';
    setFavoritesChoice(next);
    try { localStorage.setItem('bb-header-favorites', next); } catch { /* storage blocked */ }
  }, [favoritesShown]);
  // coπ: "show favorites" by voice.
  useEffect(() => {
    window.addEventListener('bb-toggle-favorites', toggleFavorites);
    return () => window.removeEventListener('bb-toggle-favorites', toggleFavorites);
  }, [toggleFavorites]);
  useEffect(() => {
    const el = headerBarRef.current;
    if (!el) return;
    const t = setTimeout(() => {
      if (el.scrollWidth <= el.clientWidth) return; // nothing to scroll
      el.scrollTo({ left: 160, behavior: 'smooth' });
      const back = setTimeout(() => el.scrollTo({ left: 0, behavior: 'smooth' }), 3000);
      return () => clearTimeout(back);
    }, 1500);
    return () => clearTimeout(t);
  }, []); // once on mount

  // The content clears the menu on whichever side it sits (left/right-handed).
  const contentMarginLeftClass = !isMobile
    ? (displayMode === 'full')
      ? "ml-[var(--sidebar-width)] menuright:ml-0 menuright:mr-[var(--sidebar-width)]"
      : "ml-[var(--sidebar-width-icon)] menuright:ml-0 menuright:mr-[var(--sidebar-width-icon)]"
    : "ml-0";

  // Apply fine tune CSS custom properties to document root
  useEffect(() => {
    if (!isProfilePage || !fineTune) return;
    const root = document.documentElement;
    const propsToSet: [string, string][] = [];
    if (fineTune.headerBgColor) propsToSet.push(['--ft-header-bg', fineTune.headerBgColor]);
    if (fineTune.headerBgImage) propsToSet.push(['--ft-header-bg-image', `url(${fineTune.headerBgImage})`]);
    if (fineTune.menuBgColor) propsToSet.push(['--ft-menu-bg', fineTune.menuBgColor]);
    if (fineTune.menuBgImage) propsToSet.push(['--ft-menu-bg-image', `url(${fineTune.menuBgImage})`]);
    if (fineTune.buttonBgColor) propsToSet.push(['--ft-button-bg', fineTune.buttonBgColor]);
    if (fineTune.buttonTextColor) propsToSet.push(['--ft-button-text', fineTune.buttonTextColor]);
    if (fineTune.bodyBgColor) propsToSet.push(['--ft-body-bg', fineTune.bodyBgColor]);
    if (fineTune.bodyBgImage) propsToSet.push(['--ft-body-bg-image', `url(${fineTune.bodyBgImage})`]);
    if (fineTune.borderColor) propsToSet.push(['--ft-border-color', fineTune.borderColor]);
    if (fineTune.borderColorFocus) propsToSet.push(['--ft-border-color-focus', fineTune.borderColorFocus]);
    if (fineTune.borderWidth && fineTune.borderWidth !== '1') propsToSet.push(['--ft-border-width', `${fineTune.borderWidth}px`]);
    propsToSet.forEach(([k, v]) => root.style.setProperty(k, v));
    return () => { propsToSet.forEach(([k]) => root.style.removeProperty(k)); };
  }, [isProfilePage, fineTune]);

  // Toggle chat — closes mobile sidebar + player sheet + theme picker if open
  const handleChatToggle = useCallback(() => {
    if (isMobile && openMobile) {
      setOpenMobile(false);
    }
    setIsChatOpen(prev => {
      // When opening chat, close the player sheet on mobile + the theme picker
      if (!prev) {
        setThemePickerOpen(false);
        if (isMobile) window.dispatchEvent(new Event('close-player-sheet'));
      }
      return !prev;
    });
  }, [isMobile, openMobile, setOpenMobile]);

  // Close sidebar + chat + theme picker when the player sheet opens
  useEffect(() => {
    const handler = () => {
      setThemePickerOpen(false);
      if (isMobile) {
        setOpenMobile(false);
        setIsChatOpen(false);
      }
    };
    window.addEventListener('close-sidebar-and-chat', handler);
    return () => window.removeEventListener('close-sidebar-and-chat', handler);
  }, [isMobile, setOpenMobile]);

  // Auto-close the theme picker on every route change — clicking another menu
  // item should leave the theme window behind, not have it follow the user.
  useEffect(() => {
    setThemePickerOpen(false);
  }, [pathname]);

  // Custom sidebar trigger — closes chat window and VU popup if open on mobile
  const handleSidebarTrigger = useCallback(() => {
    if (isMobile && isChatOpen) {
      setIsChatOpen(false);
    }
    // Close the VU meter popup whenever the sidebar/logo button is tapped
    window.dispatchEvent(new Event('close-vu-popup'));
  }, [isMobile, isChatOpen]);

  // Public product home has its own navigation and no player/chat overlays.
  if (pathname === '/lyriadj' || pathname === '/dj-lyria') {
    return <main id="main-content" className="min-h-screen">{children}</main>;
  }

  // Artist microsites (/site/*) render fully standalone — no BB sidebar, header,
  // media player, or chat. This is the artist's own branded site.
  if (pathname?.startsWith('/site/')) {
    return <div id="main-content" className="min-h-screen bg-background">{children}</div>;
  }
  // Event screens (/screens): only the films, nothing of the app around them
  // (Gordon 2026-11-03, pro-launch screens). Like /site/ and /signin, no chat
  // button here: these are unattended displays, not the app.
  if (pathname?.startsWith('/screens')) {
    return <div id="main-content" className="min-h-screen bg-black">{children}</div>;
  }

  return (
    <div
      // Sign-in: exactly the visible height (100vh on iPhones is the height
      // with the toolbars hidden, so the page could still scroll a little).
      className={cn('relative', pathname === '/signin' ? 'min-h-[100dvh]' : 'min-h-screen', isProfilePage && profileThemeClass && `profile-page-wrapper ${profileThemeClass}`)}
    >
      {/* Skip to main content — visible on focus for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:text-sm focus:font-medium focus:shadow-lg"
      >
        {tCommon('skipToContent')}
      </a>
      <div data-tour="tour-sidebar">
        <Sidebar>
          <SidebarHeader />
          <SidebarContent>
            <SidebarNav />
          </SidebarContent>
        </Sidebar>
      </div>


      {/* Fixed header — always visible regardless of scroll position */}
      {/* No data-bg="header": themes paint that with !important, and the header
          must stay see-through (Gordon 2026-10-30). */}
      <header
        className={cn(
          "fixed top-0 right-0 z-40 flex h-[var(--header-height)]",
          "transition-[left,right,background-color] duration-300 ease-in-out",
          // Always see-through — no background colour behind the messages
          // (Gordon 2026-10-30). Once content scrolls under it, a colourless
          // blur; every text and icon carries a shadow for legibility.
          "bg-transparent bb-chrome-text-shadow bb-chrome-icon-shadow",
        )}
        style={(() => {
          const offset = isMobile ? '0' : displayMode === 'full' ? 'var(--sidebar-width)' : 'var(--sidebar-width-icon)';
          return menuSide === 'right' ? { left: '0', right: offset } : { left: offset };
        })()}
      >
        {/* The header's see-through blur, the same as the footer player
            (.bb-header-glass shares .bb-player-bar's settings; Gordon
            2026-10-31: "missing translucent blur in header bg under text
            messages, like media player footer"). It is a sibling layer, never
            on the <header> itself: that would become a backdrop root and kill
            the app-menu and search buttons' own blur (.bb-glass-btn). */}
        <div aria-hidden="true" className="bb-header-glass pointer-events-none absolute inset-0 z-0" />
        {/* Visitor mode: content-thumbnail slideshow fills the header bg. */}
        {/* The message board fills the header behind its buttons — the
            whole header, or the right half (Settings → Layout). */}
        {pathname !== '/signin' && (user ? boardPrefs?.size !== 'off' : true) && (
          <div className={cn("absolute inset-y-0 right-0 z-0", user && boardPrefs?.size === 'half' ? "left-1/2" : "left-0")}>
            <HeaderBoard uid={user?.uid} prefs={boardPrefs ?? undefined} chats={boardChats} sharing={favoritesShown} />
          </div>
        )}
        {/* menuright: the menu button and search swap ends with the menu side. */}
        {/* The row itself lets clicks through to the message board behind it;
            only its buttons (and an open favorites strip) take them. */}
        <div className="relative z-[1] flex items-stretch w-full pointer-events-none menuright:flex-row-reverse" onClick={() => { if (isMobile && isChatOpen) setIsChatOpen(false); }}>
          <div className="flex flex-shrink-0 items-stretch pointer-events-auto menuright:flex-row-reverse">
            {isMobile && (
              <div onClick={handleSidebarTrigger}>
                <SidebarTrigger {...swap} />
              </div>
            )}
            <Button
              variant="ghost"
              size="fixedIcon"
              // See-through glass, lighter than search/menu (Gordon 2026-11-01: "more transparent").
              className="bb-glass-btn bb-glass-btn--light rounded-none p-0 m-0 border-0"
              onClick={toggleFavorites}
              aria-pressed={favoritesShown}
              aria-label={favoritesShown ? tNav('hideFavorites') : tNav('showFavorites')}
              title={favoritesShown ? tNav('hideFavorites') : tNav('showFavorites')}
            >
              {/* The same heart as adding a favorite (Gordon 2026-10-31): filled
                  while the favorites strip shows, outline while hidden. */}
              <Heart className={cn("h-5 w-5", favoritesShown && "fill-current")} />
            </Button>
          </div>
          <div
            ref={headerBarRef}
            className={cn(
              "flex-grow min-w-0 overflow-x-auto hide-scrollbar transition-[opacity,transform] duration-500",
              favoritesShown ? "pointer-events-auto" : "pointer-events-none",
            )}
            data-tour="tour-favorites-bar"
          >
            <div ref={favStripRef} className="h-full will-change-transform">
            {favoritesShown && pathname !== '/signin' && !pathname.startsWith('/radio/') && (user ? <FavoritesBar /> : <LoggedOutHeaderContent />)}
            </div>
          </div>
          {(brandingSettings.adBannerEnabled ?? false) && (
<div className="pointer-events-auto">
            <HeaderAdBanner
              enabled={brandingSettings.adBannerEnabled ?? false}
              cycleInterval={brandingSettings.adBannerCycleInterval ?? 12}
              displayDuration={brandingSettings.adBannerDisplayDuration ?? 5}
              expandRatio={brandingSettings.adBannerExpandRatio ?? 2}
              sources={brandingSettings.adBannerSources ?? ['marketplace', 'events']}
            />
            </div>
          )}
          <div className="flex flex-shrink-0 items-stretch pointer-events-auto">
            {/* Language picker lives in the sidebar menu only (removed from the
                header per Gordon — declutters the top bar). */}
            {/* Help mode toggle — activates tooltip bubbles across the entire app */}
            <div className="hidden sm:block">
            <HelpBubble
              title="Help Mode"
              description="Toggle in-app tooltips. When ON, hover any element to see what it does, learn more, or ask the BB AI. No dots — the highlighted Help button in the header is the only indicator."
              learnMoreAnchor="getting-started"
              side="bottom"
            >
              <Button
                variant="ghost"
                size="fixedIcon"
                className={cn(
                  // Glass + outline like the search button beside it (Gordon 2026-11-01).
                  "bb-edge-btn bb-glass-btn p-0 m-0 transition-colors",
                  // Active state: primary outline (replaces per-element dots).
                  helpEnabled && "!border-primary"
                )}
                onClick={toggleHelp}
                aria-label={helpEnabled ? tCommon('helpTurnOff') : tCommon('helpTurnOn')}
                aria-pressed={helpEnabled}
                title={helpEnabled ? tCommon('helpOnTitle') : tCommon('helpOffTitle')}
              >
                <HelpCircle className={cn("h-5 w-5", helpEnabled && "text-primary")} />
              </Button>
            </HelpBubble>
            </div>
            <HelpBubble
              title="Search"
              description="Search across all artists, music, events, marketplace items, and news on Breakin' Bread."
              learnMoreAnchor="search"
              aiQuestion="How do I find specific music or artists on Breakin' Bread?"
              side="bottom"
            >
              {/* A toggle (Gordon 2026-10-31): on /search it turns into an X that
                  closes search — back where you were, or Home from outside BB. */}
              {pathname === '/search' ? (
                <Button variant="ghost" size="fixedIcon" aria-label={tCommon('close')} title={tCommon('close')} className="bb-edge-btn bb-glass-btn p-0 m-0 flex-shrink-0" {...swap}
                  onClick={() => { if (window.history.length > 1) router.back(); else router.push('/home'); }}>
                  <X className="h-5 w-5"/>
                </Button>
              ) : (
                <Link href="/search" aria-label={tCommon('search')} className="block flex-shrink-0" onClick={() => { if (isMobile && isChatOpen) setIsChatOpen(false); }}>
                  {/* High-contrast outline so the search button reads as
                      clickable at a glance (Gordon). */}
                  <Button variant="ghost" size="fixedIcon" aria-label={tCommon('search')} className="bb-edge-btn bb-glass-btn p-0 m-0" {...swap}>
                    <SearchIcon className="h-5 w-5"/>
                  </Button>
                </Link>
              )}
            </HelpBubble>
          </div>
        </div>
      </header>

      {/* sm:pr offsets the 50px VU strip column on the right when it is visible (isPlaying).
          When not playing the strip is hidden so the content expands to full width.
          Mobile: strip is always hidden so no right padding needed (handled separately). */}
      {/* transition-[margin-left,padding-right] instead of transition-all — avoids iOS Safari
          creating a compositing layer that breaks position:fixed on descendant elements */}
      <div className={cn(
        "transition-[margin-left,margin-right,padding-left,padding-right] duration-300 ease-in-out bg-background",
        (isPlaying || micLive) ? "sm:pr-[var(--sidebar-width-icon)] menuright:sm:pr-0 menuright:sm:pl-[var(--sidebar-width-icon)]" : "",
        contentMarginLeftClass,
      )}>
        {/* Spacer so page content starts below the fixed header */}
        <div className="h-[var(--header-height)]" aria-hidden="true" />
        {/* Outdated-browser notice — feature-detected, not user-agent sniffed.
            Sits above the PWA reminder so it's the first thing an affected
            visitor sees. Plain (non-fixed) so it never competes with the
            chat FAB (z-[105]) or mobile player bar (z-[100]) — it just pushes
            content down like any other in-flow element. */}
        {pathname !== '/signin' && <OutdatedBrowserBanner />}
        {/* Desktop PWA reminder banner — sits above main content */}
        {pathname !== '/signin' && <PWADesktopReminder />}
        {/* Global bottom clearance so page content is never hidden behind the
            fixed bottom chrome (mobile player bar + VU strip + bottom nav +
            iOS safe-area), on every page and every breakpoint. Bigger when the
            media player bar is up. Responsive via breakpoints (not JS isMobile)
            so phone / tablet / desktop all adapt. */}
        <main
          id="main-content"
          data-bg="body"
          className={cn(
            "relative",
            // The bottom padding is published as --main-pb so a page's
            // min-h-screen can subtract it (globals.css) — short pages fit
            // without scrolling (Gordon 2026-10-31: search, Angels).
            pathname === '/signin'
              ? ""
              : isMediaPlayerActive
                ? "[--main-pb:calc(140px_+_env(safe-area-inset-bottom))] sm:[--main-pb:120px] pb-[var(--main-pb)]"
                : "[--main-pb:calc(80px_+_env(safe-area-inset-bottom))] sm:[--main-pb:4rem] pb-[var(--main-pb)]",
          )}
        >
           {children}
        </main>
      </div>
      {/* coπ — the floating, draggable voice microphone (all users and visitors). */}
      {pathname !== '/signin' && <CopiMic />}
      {/* Receipt Capture — opened anywhere by `bb-snap` (coπ "snap a receipt", /bookkeeping, /platform). */}
      <ReceiptSnapHost />
      {/* One share panel for every share in the app: send to a BB member, copy, share via…, QR. */}
      <ShareSheetHost />
      {pathname !== '/signin' && (
        <div data-tour="tour-media-player" className="contents">
          <MediaPlayer />
        </div>
      )}

      {/* Global Help layer — when Help Mode is ON, hovering any button / link /
          [role=button] shows an inline help bubble using its title / aria-label /
          data-help* metadata. Elements wrapped in <HelpBubble> opt out via the
          data-help-bubble-wrapped marker. */}
      <AutoHelpLayer />

      {/* Contrast Vector overlay — theme-aware SVG accent layer that reacts to
          device tilt (mobile) / mouse (desktop) and the media player audio.
          Pointer-events: none so it never intercepts UI. User toggle persists.
          Skipped in safe mode (crash-loop recovery). */}
      {/* ContrastVectorMount disabled entirely per Gordon ("turn off any
          vector backgrounds") — decorative full-screen animated SVG layer,
          removed while stabilising rendering load. Re-enable by restoring:
          {pathname !== '/signin' && !safeMode && <ContrastVectorMount />} */}

      {/* DJ mini-bar — shown on mobile when DJ mixer is playing but no normal track is loaded.
          Matches the mobile player bar height (56px) and leaves the left 56px for the chat FAB. */}
      {isDJMode && djTrackInfo && !isMediaPlayerActive && (
        <div className="fixed bottom-0 right-0 w-[calc(100%-56px)] h-[56px] z-[100] bg-card border-t border-border flex items-center px-3 gap-2 sm:hidden menuright:right-auto menuright:left-0">
          <span className="text-[10px] font-black bg-cyan-500 text-black rounded px-1.5 py-0.5 flex-shrink-0 uppercase tracking-widest">DJ</span>
          <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
            <span className="text-[11px] font-semibold text-foreground truncate leading-tight">{djTrackInfo.title}</span>
            {djTrackInfo.artist && (
              <span className="text-[9px] text-muted-foreground truncate leading-tight">{djTrackInfo.artist}</span>
            )}
          </div>
          <span className="text-[9px] text-muted-foreground flex-shrink-0 animate-pulse">▶ live</span>
        </div>
      )}

      {/* Global incoming call overlay — rings on ANY page */}
      {user && <GlobalCallOverlay />}

      {/* Global active call UI — WebRTC audio/video on ANY page */}
      {user && activeCallId && (
        <ActiveCallUI
          callId={activeCallId}
          onCallEnded={() => setActiveCallId(null)}
        />
      )}

      {/* VU strips are exonerated by the tier-ladder bisection data (a WebKit
          device that crashes at tier 2 runs tier 1 — VU on — stably), so they
          no longer wait for tier 1: every device gets them except safe mode. */}
      {pathname !== '/signin' && !safeMode && !perfExp.has('novu') && (
        <div data-tour="tour-vu-strip" className="contents">
          <GlobalVuStrip playerActive={isMediaPlayerActive} />
        </div>
      )}
      {/* Chat toggle button — visible everywhere EXCEPT /signin (clicking it there would cause a redirect loop).
          Logged-in: opens/closes chat. Visitor: redirects to sign-in.
          z-[105] keeps it above the chat window (z-[55]) and player bar (z-[100]).
          Docked bottom-LEFT, next to the sidebar (Gordon: "next to side menu left
          aligned") — mirrors the play/toggle button, which now docks bottom-right.
          On mobile the chat window stops at bottom-[76px] so the 56px FAB is never covered. */}
      {pathname !== '/signin' && (
        <HelpBubble
          title="Chat & BB AI"
          description="Open the chat to message other users and artists, or ask the BB AI Advisor about music business, licensing, and more."
          learnMoreAnchor="chat"
          aiQuestion="How does the chat work on Breakin' Bread?"
          side="left"
        >
        <Button
          variant="default"
          className={cn(
            "fixed bottom-0 left-0 z-[105] !rounded-none !p-0 !m-0 shadow-lg transition-all duration-300 overflow-hidden menuright:left-auto menuright:right-0",
            // Theme-adaptive with a stronger, higher-contrast outline than the
            // app's usual border-border outline buttons — Gordon: "outline the
            // chat button for better visibility" (it sits off to the side now,
            // away from the player bar's visual weight).
            "bg-card text-foreground border-2 border-primary hover:bg-muted",
            isMobile
              ? "h-[56px] w-[56px]"
              : "h-[var(--player-height)] w-[var(--sidebar-width-icon)]"
          )}
          aria-label={isChatOpen ? tChat('close') : 'Open chat'}
          data-bb-chat-fab=""
          {...swap}
          onClick={user
            // Opening chat also closes an open mobile menu — the off-canvas
            // sidebar would otherwise sit on top of the chat window (Gordon).
            ? () => { window.dispatchEvent(new Event('close-vu-popup')); setOpenMobile(false); handleChatToggle(); }
            : () => { window.dispatchEvent(new Event('close-vu-popup')); setOpenMobile(false); setGuestChatPopup(true); }}
        >
          <MessageSquare className={cn("relative z-10 !size-[1.3rem] transition-transform", fabAnim)} />
          {user && unreadCount > 0 && !isChatOpen && (
            <span className="absolute top-1 right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-destructive text-destructive-foreground">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
        </HelpBubble>
      )}
      {user && isChatOpen && (<ChatWindow
          isOpen={isChatOpen}
          setIsOpen={setIsChatOpen}
          initialRoomId={initialChatRoomId}
          initialTargetUserId={initialChatTargetUserId}
          onRoomOpened={() => { setInitialChatRoomId(null); setInitialChatTargetUserId(null); }}
          copilotRequest={copilotRequest}
          onCopilotRequestHandled={() => setCopilotRequest(null)}
        />
      )}

      {/* Visitor chat info popup — shown when a logged-out user clicks the chat FAB */}
      {guestChatPopup && (
        <div
          className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4"
          onClick={() => setGuestChatPopup(false)}
        >
          <div
            className="relative bg-card border border-border rounded-2xl shadow-2xl p-6 max-w-sm w-full mb-[56px] sm:mb-0"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setGuestChatPopup(false)}
              className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-sm text-foreground">Join to chat</p>
                <p className="text-xs text-muted-foreground">Free account required</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Sign in and follow artists or users to start messaging. Chat with creators, fans, and the BB AI Advisor — all in one place.
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="flex-1"
                onClick={() => { setGuestChatPopup(false); router.push('/signin'); }}
              >
                Sign in
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1"
                onClick={() => { setGuestChatPopup(false); router.push('/onboarding'); }}
              >
                Create account
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* No welcome / What's-New overlays (Gordon 2026-10-29: "less pop ups").
          What's New now runs as update slides in the home hero, and visitors
          get the hero intro + VisitorWelcome instead of a modal. */}

      {/* Silently tracks anonymous page views for the Visitor Intel dashboard */}
      <GuestTracker />

      {/* ── Mobile footer nav — slide-up icon tray above player bar ─────────
          The swipe-handle strip (z-[101], over the player bar's z-[100]) and
          all drag tracking live INSIDE MobileFooterNav itself — this used to
          also be duplicated here as a second, pixel-identical fixed strip at
          the same z-index, which put two independent touch handlers fighting
          over the same 8px hit area (one thresholded start/end pair here, a
          separate one in the component) and could leave the tray's transform
          stuck mid-transition. One implementation now. */}
      {pathname !== '/signin' && (
        <MobileFooterNav
          open={mobileMenuOpen}
          onOpen={() => setMobileMenuOpen(true)}
          onClose={() => setMobileMenuOpen(false)}
        />
      )}

      {/* ThemePicker rendered at root level — survives mobile sidebar Sheet close */}
      <ThemePicker
        open={themePickerOpen}
        onClose={() => setThemePickerOpen(false)}
        sidebarExpanded={!isMobile && displayMode !== 'icon'}
        isMobile={isMobile}
      />

      {tourOpen && (
        <GuidedTour
          startStep={tourStartStep}
          onClose={() => setTourOpen(false)}
        />
      )}

       <style jsx global>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
        /* Global scrollbar hiding */
        ::-webkit-scrollbar {
            width: 0px;
            height: 0px;
            background: transparent; /* make scrollbar transparent */
        }
        * {
          scrollbar-width: none; /* For Firefox */
          -ms-overflow-style: none; /* For Internet Explorer and Edge */
        }
      `}</style>
    </div>
  );
}

export function AppShell({ children }: PropsWithChildren) {
  return (
    <SidebarProvider defaultDisplayMode='icon'>
      <AppShellLayout>
        {children}
      </AppShellLayout>
    </SidebarProvider>
  );
}
